import React from 'react'
import { useTranslation } from 'react-i18next'
import { PagingSidePanelType } from '@node-space/storybook-components/dist/components/SidePanel/types'
import CopyButton from '@node-space/storybook-components/dist/CopyButton'
import { SidePanel } from '@node-space/storybook-components/dist/SidePanel'
import { Avatar, Box, Text } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { SidePanelBody } from 'components/SidePanel/SidePanelBody'
import { SidePanelSkeletonLoader } from 'components/SidePanel/SidePanelSkeletonLoader'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useEmbeddedCustomerAgreementsQuery } from 'hooks/queries/useEmbeddedCustomerAgreementsQuery'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { BusinessCustomerType } from 'types/customers'
import { highlightRowBasedOnId } from 'utils/table/table'
import { getAvatarInitial } from 'utils/utils'
import { EmbeddedCustomerTypes } from '../helpers/constants'
import { EmbeddedCustomer } from '../helpers/types'
import { BusinessCustomer } from './BusinessCustomer'
import { CustomerStatusHighlight } from './CustomerHighlight'
import { EmbeddedCustomerAgreements } from './EmbeddedCustomerAgreements'

export interface EmbeddedCustomerDetailsProps {
  isOpen: boolean
  onClose: () => void
  pageCustomers: PagingSidePanelType
  customerDetails: EmbeddedCustomer
}

export const EmbeddedCustomerDetails = ({
  isOpen,
  onClose,
  pageCustomers,
  customerDetails,
}: EmbeddedCustomerDetailsProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)

  const { name, id: customerReference } = customerDetails || {}
  // TODO: handle error states
  const { data: agreementsData = [], isFetching: isFetchingAgreements } =
    useEmbeddedCustomerAgreementsQuery(customerReference)
  const { data: customerData, isFetching: isFetchingCustomerData } =
    useCustomerQuery(customerReference)

  const company =
    customerDetails?.type === EmbeddedCustomerTypes.BUSINESS &&
    (customerData as BusinessCustomerType)?.company

  const sumsubUrl = 'https://in.sumsub.com/websdk/p/uni_lumfNwKA2I2xA354'

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      paging={pageCustomers}
      activeRowId={customerDetails?.id}
      onActiveRowChange={highlightRowBasedOnId}
      externalStyle={{ top: `${bannerSpacing}px` }}
      sidePanelHeader={{
        customSidePanelHeader: (
          <Box flex align="center" gap={16}>
            <Avatar size="large" type="entity" name={getAvatarInitial(name)} />
            <Box>
              <Text variant="heading-2">{name}</Text>
              <Text color="subdued">{customerDetails?.description}</Text>
            </Box>
          </Box>
        ),
      }}
    >
      <SidePanelBody flexCol gap={12}>
        {isFetchingAgreements || isFetchingCustomerData ? (
          <SidePanelSkeletonLoader />
        ) : (
          <>
            {customerDetails?.type === EmbeddedCustomerTypes.BUSINESS && (
              <BusinessCustomer company={company} />
            )}
            <Box flex align="center" gap={8}>
              <Text variant="heading-4">{t('embeddedCustomers.details.kybLink')}</Text>
              <CustomerStatusHighlight status={customerDetails?.status} />
            </Box>
            <Box flex align="center" paddingY={8}>
              <Text color="subdued">{sumsubUrl}</Text>
              <CopyButton copyText={sumsubUrl} confirmationMessage={t('copiedToClipboard')} />
            </Box>
            {!!agreementsData?.length && (
              <>
                <Text variant="heading-4">{t('legalAgreements.received')}</Text>
                <EmbeddedCustomerAgreements agreements={agreementsData} />
              </>
            )}
          </>
        )}
      </SidePanelBody>
    </SidePanel>
  )
}
